import React, { useEffect, useState } from 'react';
import { CQMgmtServiceProvider } from 'services/cq-mgmt.service';
import { Button, IconSettings, Modal, Tooltip } from '@salesforce/design-system-react';
import { useTranslation } from 'react-i18next';
import { SFPackageVersion } from 'sf-app/sf-package';
import { ICQCurrentOrgInfoModalProps } from 'interfaces/IModal.interface';
import { IExtensionInfoMessage } from 'interfaces/IUpgradeSchema.interface';
import { CQAppConstant } from '../../app-constants';

/**
 * CQCurrentOrgInfoModal component displays a modal with information about the currently installed ComplianceQuest package
 * and other installed packages in the organization.
 * @function
 * @name CQCurrentOrgInfoModal
 * @description
 * The modal displays the name and version of the ComplianceQuest package if installed, and a list of other installed packages.
 * The modal can be toggled open and closed using a button.
 */

function CQCurrentOrgInfoModal(props: ICQCurrentOrgInfoModalProps) {
    const { t } = useTranslation();
    const [installedPackages, setInstalledPackages] = useState<{ name: string; version: SFPackageVersion }[]>([]);
    const {
        alertMode = false,
        modalOpenState,
        toggleModalState,
        onProceed,
        onMigrationLogin,
        showMigrationLogin,
        migrationLoginStatus,
        migrationUsername,
        migrationLoginError,
        proceedDisabled,
        manualTask,
        infoMessage,
        orgInfo
    } = props;

    useEffect(() => {
        setInstalledPackages(CQMgmtServiceProvider.getCachedInstalledPackages());
    }, []);

    /**
     * Renders a list of installed packages with their names and versions.
     *
     * @returns A JSX element containing a grid of installed packages.
     *
     * Each package is displayed in a list item (`<li>`) with its name and version.
     * The version is formatted as `majorVersion.minorVersion.patchVersion`.
     *
     * The component uses Salesforce Lightning Design System (SLDS) classes for styling.
     */
    function installedPackagesList() {
        return (
            <div className="slds-p-around_small slds-m-left_small">
                <div className="slds-grid slds-wrap">
                    {installedPackages.map((pkg) => (
                        <div key={pkg.name} className="slds-col slds-size_1-of-2 slds-p-around_xx-small">
                            <li>
                                {pkg.name} v{pkg.version.majorVersion}.{pkg.version.minorVersion}.{pkg.version.patchVersion}
                            </li>
                        </div>
                    ))}
                </div>
            </div>);
    }

    /**
     * Toggles the state of the modal and optionally calls the onProceed callback.
     *
     * This function first toggles the modal state by calling `toggleModalState()`.
     * If the `onProceed` callback is provided, it will be invoked after the modal state is toggled.
     */
    function toggleModal (){
        toggleModalState();
        onProceed?.();
    };

    /**
     * Renders a message based on the alert mode status.
     *
     * @returns {JSX.Element} A JSX element containing a message.
     * If `alertMode` is true, it displays a confirmation message.
     * If `alertMode` is false, it displays the package name and version or a default message if not available.
     */
    function alertModeMethod() {
        return (alertMode ? (
            <div className='slds-text-heading_medium'>
                <strong>{t("org_confirm")}</strong>
            </div>
        ) : (
            <div className='slds-text-align_left slds-text-heading_medium '>
                <strong>{`${props.packageNameAndVersion || CQAppConstant.NO_PACKAGE}`} {t("org_install_info")}</strong>
            </div>));
    }

    /**
     * Generates the footer content for a modal based on the current alert mode state.
     *
     * @returns {JSX.Element} The footer element containing buttons. 
     * If `alertMode` is true, it renders "Cancel" and "Continue" buttons. 
     * Otherwise, it renders a single "Close" button.
     */
    function getFooter() {
        return alertMode ? (
            <div>
                <Button key="cancel" label={CQAppConstant.BUTTON_LABELS.CANCEL} onClick={toggleModalState} variant="neutral" />
                {showMigrationLogin && (
                    <span className="slds-m-left_x-small slds-m-right_x-small">
                        <Tooltip
                            id="migration-login-tooltip"
                            content={
                                <span>
                                    <strong>{t('migration_login_note_label')}</strong> {t('migration_login_note')}
                                </span>
                            }
                            align="top"
                        >
                            <Button
                                key="migration-login"
                                label={migrationLoginStatus === CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS
                                    ? t('migration_login_button_success')
                                    : t('migration_login_button')}
                                onClick={onMigrationLogin}
                                variant="neutral"
                                disabled={migrationLoginStatus === CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS}
                            />
                        </Tooltip>
                    </span>
                )}
                <Button
                    key="continue"
                    label={CQAppConstant.BUTTON_LABELS.CONTINUE}
                    onClick={() => toggleModal()}
                    variant="brand"
                    disabled={proceedDisabled}
                />
            </div>
        ) : (
            <Button key="close" label={CQAppConstant.BUTTON_LABELS.CLOSE} onClick={toggleModalState} variant="brand" />
        );
    }

    /**
     * Renders user and organization information in a list format.
     * 
     * Depending on the `alertMode` flag, it displays different sets of information.
     * 
     * @returns A JSX element containing the organization and user information.
     * 
     * @remarks
     * - When `alertMode` is true, it displays the organization name, type, user information, and a placeholder for installed packages.
     * - When `alertMode` is false, it displays the organization name, type, and a placeholder for installed packages.
     */
    function userAndOrgInformation() {
        const listPadding = "slds-p-around_x-small";
        return (
            <>
                <ul className="slds-text-heading_small">
                    <li className={listPadding}><strong>{t("org_name")}</strong> {orgInfo.name}</li>
                    <li className={listPadding}><strong>{t("org_type")}</strong> {orgInfo.isSandbox}</li>
                    {alertMode &&
                        <li className={listPadding}><strong>{t("org_login")}</strong> {orgInfo.userName}/{orgInfo.fullName}/{orgInfo.profileName}</li>
                    }
                    <li className={listPadding}><strong>{t("org_packages")}</strong> </li>
                </ul>
            </>
        );
    }


    /**
     * Renders the body content of the modal dialog.
     *
     * This function composes and returns a React fragment containing:
     * - User and organization information (via `userAndOrgInformation()`).
     * - A list of installed packages (via `installedPackagesList()`).
     * - Conditional manual task information (via `manualTaskInfo()`), displayed only if `alertMode` and `manualTask` are truthy.
     *
     * @returns {JSX.Element} The modal body as a React fragment.
     */
    function getModalBody() {
        return (
            <>
                {userAndOrgInformation()}
                {installedPackagesList()}
                {renderMigrationLoginStatus()}
                {infoMessage && infoMessage.length > 0 && renderModuleInfoMessage(infoMessage)}
                {alertMode && manualTask && manualTaskInfo()}
            </>
        );
    }

    /**
     * Renders the migration login status alert based on the current login state.
     *
     * @returns A success/error alert element, or null when no status is available.
     */
    function renderMigrationLoginStatus() {
        if (!showMigrationLogin || !migrationLoginStatus) return null;
        if (migrationLoginStatus === CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS) {
            return (
                <div className="slds-notify slds-notify_alert slds-theme_success slds-m-top_small" role="alert">
                    <span className="slds-assistive-text">{CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS}</span>
                    <span>{t('migration_login_runs_as', { username: migrationUsername || t('migration_login_current_user') })}</span>
                </div>
            );
        }
        if (migrationLoginStatus === CQAppConstant.MIGRATION_LOGIN.STATUS.ERROR) {
            return (
                <div className="slds-notify slds-notify_alert slds-theme_error slds-m-top_small" role="alert">
                    <span className="slds-assistive-text">{CQAppConstant.MIGRATION_LOGIN.STATUS.ERROR}</span>
                    <span>{migrationLoginError || t('migration_login_failed')}</span>
                </div>
            );
        }
        return null;
    }

    /**
     * Renders a list containing information about a manual task step.
     * The list displays a note with a localized message using the `t` translation function.
     *
     * @returns {JSX.Element} An unordered list with a note about manual step information.
     */
    function manualTaskInfo() {
        return (
            <ul className="slds-text-heading_small">
                <li className="slds-p-around_x-small"><strong>Note : </strong>{t("manual_step_info")}</li>
            </ul>);
    }

    /**
     * Renders a warning alert message with Salesforce Lightning Design System (SLDS) styles.
     *
     * @param infoMessages - License info messages to display.
     * @returns The info alert element containing the messages.
     */
    function renderModuleInfoMessage(infoMessages: IExtensionInfoMessage[]) {
        return (
            <div className="slds-notify slds-notify_alert slds-theme_alert-texture slds-theme_info" role="alert">
                <span className="slds-assistive-text">Info</span>
                <span className="slds-icon_container slds-icon-utility-info slds-m-right_small" title="Info">
                    <svg className="slds-icon slds-icon_x-small" aria-hidden="true">
                        <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#info"></use>
                    </svg>
                </span>
                {infoMessages.length === 1
                    ? <span>{t('license_single_message', { message: infoMessages[0].message })}</span>
                    : <>
                        <div className='slds-text-align_left'>
                            <div>{t('license_ensure_multiple')}</div>
                            <ul className="slds-list_dotted slds-m-top_x-small">
                                {infoMessages.map((entry) => (
                                    <li key={entry.packageName}>
                                        {t('license_for_package', { packageName: entry.packageName, message: entry.message })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                }
            </div>
        );
    }

    return (
        <>
            <IconSettings iconPath="/assets/icons">
                <Modal
                    isOpen={modalOpenState}
                    onRequestClose={toggleModalState}
                    heading={alertModeMethod()}
                    assistiveText={{ closeButton: 'Close' }}
                    size="small"
                    footer={getFooter()}>
                    {getModalBody()}
                </Modal>
            </IconSettings>
            {!alertMode && (<Button label={t("org_info")} variant="brand" onClick={toggleModalState} />)}
        </>
    );
}

export default CQCurrentOrgInfoModal;